// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Edit, Trash2 } from 'lucide-react';

interface EventFAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

interface FAQFormData {
  question: string;
  answer: string;
  category: string;
  is_active: boolean;
}

const EventFAQsPage: React.FC = () => {
  const [faqs, setFaqs] = useState<EventFAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedFAQ, setSelectedFAQ] = useState<EventFAQ | null>(null);
  const [formData, setFormData] = useState<FAQFormData>({
    question: '',
    answer: '',
    category: 'general',
    is_active: true
  });
  const [submitting, setSubmitting] = useState(false);

  // Fetch FAQs from Supabase
  const fetchFAQs = async () => {
    try {
      console.log('Fetching FAQs...');
      const { data, error } = await supabase
        .from('event_faqs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching FAQs:', error);
        toast.error('Failed to load FAQs');
        return;
      }

      console.log('FAQs loaded:', data);
      setFaqs(data || []);
    } catch (error) {
      console.error('Exception fetching FAQs:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Load FAQs on component mount
  useEffect(() => {
    fetchFAQs();
  }, []);

  // Reset form data
  const resetForm = () => {
    setFormData({
      question: '',
      answer: '',
      category: 'general',
      is_active: true
    });
  };

  // Handle add FAQ
  const handleAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  // Handle edit FAQ
  const handleEdit = (faq: EventFAQ) => {
    setSelectedFAQ(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category,
      is_active: faq.is_active
    });
    setShowEditModal(true);
  };

  // Handle delete FAQ
  const handleDelete = (faq: EventFAQ) => {
    setSelectedFAQ(faq);
    setShowDeleteDialog(true);
  };

  // Submit form (add or edit)
  const handleSubmit = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      toast.error('Question and answer are required');
      return;
    }

    setSubmitting(true);

    try {
      if (showEditModal && selectedFAQ) {
        // Update existing FAQ
        const { error } = await supabase
          .from('event_faqs')
          .update({
            question: formData.question.trim(),
            answer: formData.answer.trim(),
            category: formData.category,
            is_active: formData.is_active,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedFAQ.id);

        if (error) {
          console.error('Error updating FAQ:', error);
          toast.error('Failed to update FAQ');
          return;
        }

        toast.success('FAQ updated successfully');
        setShowEditModal(false);
      } else {
        // Create new FAQ
        const { error } = await supabase
          .from('event_faqs')
          .insert([{
            question: formData.question.trim(),
            answer: formData.answer.trim(),
            category: formData.category,
            is_active: formData.is_active
          }]);

        if (error) {
          console.error('Error creating FAQ:', error);
          toast.error('Failed to create FAQ');
          return;
        }

        toast.success('FAQ created successfully');
        setShowAddModal(false);
      }

      // Refresh the FAQ list
      await fetchFAQs();
      resetForm();
    } catch (error) {
      console.error('Exception submitting FAQ:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!selectedFAQ) return;

    setSubmitting(true);

    try {
      const { error } = await supabase
        .from('event_faqs')
        .delete()
        .eq('id', selectedFAQ.id);

      if (error) {
        console.error('Error deleting FAQ:', error);
        toast.error('Failed to delete FAQ');
        return;
      }

      toast.success('FAQ deleted successfully');
      setShowDeleteDialog(false);
      
      // Refresh the FAQ list
      await fetchFAQs();
    } catch (error) {
      console.error('Exception deleting FAQ:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setSubmitting(false);
      setSelectedFAQ(null);
    }
  };

  // Cancel modals
  const handleCancel = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteDialog(false);
    setSelectedFAQ(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-2 text-muted-foreground">Loading FAQs...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Event FAQs</h1>
        <Button onClick={handleAdd} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add FAQ
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>FAQ Management</CardTitle>
        </CardHeader>
        <CardContent>
          {faqs.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No FAQs found. Click "Add FAQ" to create your first FAQ.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Question</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {faqs.map((faq) => (
                  <TableRow key={faq.id}>
                    <TableCell className="font-medium max-w-md">
                      <div className="truncate" title={faq.question}>
                        {faq.question}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="capitalize">{faq.category}</span>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        faq.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {faq.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {new Date(faq.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(faq)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(faq)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add FAQ Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New FAQ</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="question">Question *</Label>
              <Input
                id="question"
                value={formData.question}
                onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                placeholder="Enter the FAQ question"
              />
            </div>
            <div>
              <Label htmlFor="answer">Answer *</Label>
              <Textarea
                id="answer"
                value={formData.answer}
                onChange={(e) => setFormData(prev => ({ ...prev, answer: e.target.value }))}
                placeholder="Enter the FAQ answer"
                rows={4}
              />
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full border rounded p-2"
              >
                <option value="general">General</option>
                <option value="registration">Registration</option>
                <option value="payment">Payment</option>
                <option value="venue">Venue</option>
                <option value="schedule">Schedule</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Creating...' : 'Create FAQ'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit FAQ Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit FAQ</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-question">Question *</Label>
              <Input
                id="edit-question"
                value={formData.question}
                onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                placeholder="Enter the FAQ question"
              />
            </div>
            <div>
              <Label htmlFor="edit-answer">Answer *</Label>
              <Textarea
                id="edit-answer"
                value={formData.answer}
                onChange={(e) => setFormData(prev => ({ ...prev, answer: e.target.value }))}
                placeholder="Enter the FAQ answer"
                rows={4}
              />
            </div>
            <div>
              <Label htmlFor="edit-category">Category</Label>
              <select
                id="edit-category"
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full border rounded p-2"
              >
                <option value="general">General</option>
                <option value="registration">Registration</option>
                <option value="payment">Payment</option>
                <option value="venue">Venue</option>
                <option value="schedule">Schedule</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit-is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              />
              <Label htmlFor="edit-is_active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Updating...' : 'Update FAQ'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete FAQ</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this FAQ? This action cannot be undone.
              <br />
              <br />
              <strong>Question:</strong> {selectedFAQ?.question}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              disabled={submitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {submitting ? 'Deleting...' : 'Delete FAQ'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EventFAQsPage;
